/* eslint-disable @typescript-eslint/ban-types */
import React, { ReactNode, createContext, useContext, useState } from 'react';
import { IntegrationResponse } from './Integrations.model';
import { useLocation, useNavigate } from 'react-router-dom';

/*
 * sections should be in kebab-case to follow url path convention
 * */
export enum SettingsSection {
  integrations = 'integrations',
  account = 'account',
  appSettings = 'app-settings',
  userSettings = 'user-settings',
  documentSettings = 'document-settings'
}

export interface SettingsProviderData {
  activeSettingsTab: SettingsSection | undefined;
  integrations: IntegrationResponse[];
  isLoading: boolean;
}

export interface SettingsContextProps {
  data: SettingsProviderData;
  updateSettingsTab: (tab: SettingsSection) => void;
  updateIntegrations: Function;
  updateIsLoading: Function;
}

export const defaultProviderData = {
  integrations: [
    {
      id: 'quickbooks',
      name: 'quickbooks',
      isEnabled: false
    }
  ],
  activeSettingsTab: SettingsSection.account,
  isLoading: false
};

export const SettingsContext = createContext<SettingsContextProps>({
  data: defaultProviderData,
  updateSettingsTab: Function,
  updateIntegrations: Function,
  updateIsLoading: Function
});

SettingsContext.displayName = 'SettingsProvider';

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [providerValue, setProviderValue] = useState(defaultProviderData);
  const navigate = useNavigate();
  const locationState = useLocation();

  /**
   *
   * @param tab settings section selected
   */
  const updateSettingsTab = (activeSettingsTab: SettingsSection): void => {
    // with this avoid change the url to default (/settings/account) when the page refresh
    navigate(`/settings/${activeSettingsTab}`, {
      state: activeSettingsTab === SettingsSection.appSettings ? locationState.state : undefined
    });

    setProviderValue((prevValues) => {
      return { ...prevValues, activeSettingsTab };
    });
  };

  /*
   * update integrations data with the new state
   * @param integrations[]
   */
  const updateIntegrations = (integrations: IntegrationResponse[]): void => {
    setProviderValue((prevValues) => {
      return { ...prevValues, integrations };
    });
  };

  /**
   * Updates isLoading status
   * @param isLoading Shows true when data is being retrieved or updated
   * and false if data is ready.
   */
  const updateIsLoading = (isLoading: boolean): void => {
    setProviderValue((prevValues) => {
      return { ...prevValues, isLoading };
    });
  };

  const providerData = {
    data: providerValue,
    updateSettingsTab,
    updateIntegrations,
    updateIsLoading
  };

  return (
    <SettingsContext.Provider value={providerData}>
      {!providerData.data.isLoading && children}
    </SettingsContext.Provider>
  );
};

export default function useSettings(): SettingsContextProps {
  return useContext(SettingsContext);
}
